import React, { useEffect, useRef, useState, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../core/api';
import { pixel, genEventId } from '../core/pixel';

const PAYPAL_CLIENT_ID = 'AS9sUykdkWmO5pN_yXekpRd1wMfxZL6AGsUd5ph4L2o9hqnPLVcqeumCEr-S3h18U5GHdzcyxHrmF0Wt';

const PLAN_IDS: Record<string, string> = {
    pro:          'P-61A48098A9759621FNHAYTMQ',
    basic:        'P-5FE30730C1764551JNHA4VPQ',
    pro_early:    'P-84S49612GF579180YNHBB6LA',
    // Planes anuales
    basic_annual: 'P-8MF62777SA961864NNI7QRNQ',
    pro_annual:   'P-472908317K2346840NI7QNXA',
};

const PLAN_VALUES: Record<string, number> = {
    pro:          179,
    basic:        99,
    pro_early:    149,
    basic_annual: 990,
    pro_annual:   1790,
};

interface Props {
    plan: 'pro' | 'basic' | 'pro_early' | 'basic_annual' | 'pro_annual';
    onSuccess?: () => void;
    onError?: (err: unknown) => void;
}

const PayPalButton: React.FC<Props> = ({ plan, onSuccess, onError }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const uid = useId().replace(/:/g, '');
    const containerId = `paypal-btn-${uid}`;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const planId = PLAN_IDS[plan];
        if (!planId) {
            setError('Plan no disponible aún. Contáctanos por WhatsApp.');
            setLoading(false);
            return;
        }

        let cancelled = false;

        const renderButton = () => {
            if (cancelled || !containerRef.current || !(window as any).paypal) return;
            // Limpiar renders previos
            containerRef.current.innerHTML = `<div id="${containerId}"></div>`;

            (window as any).paypal.Buttons({
                style: {
                    shape: 'pill',
                    color: 'blue',
                    layout: 'vertical',
                    label: 'paypal',
                },
                onClick: () => {
                    const eventId = genEventId();
                    const value = PLAN_VALUES[plan] ?? 99;
                    pixel.initiateCheckout(value);
                    api.post('/billing/initiate-checkout', { plan, eventId }).catch(() => {});
                },
                createSubscription: (_data: unknown, actions: any) => {
                    return actions.subscription.create({ plan_id: planId });
                },
                onApprove: async (data: any) => {
                    try {
                        await api.post('/billing/paypal/activate', {
                            subscriptionId: data.subscriptionID,
                            plan,
                        });
                        onSuccess?.();
                        navigate('/checkout-success');
                    } catch (err: any) {
                        const msg = err.response?.data?.error || 'Error activando suscripción';
                        setError(msg);
                        onError?.(err);
                    }
                },
                onError: (err: unknown) => {
                    setError('Hubo un problema con el pago. Intenta de nuevo.');
                    onError?.(err);
                },
                onCancel: () => {
                    setError(null); // reset silencioso si cancela
                },
            })
                .render(`#${containerId}`)
                .then(() => { if (!cancelled) setLoading(false); })
                .catch(() => { if (!cancelled) setError('No se pudo cargar el botón de pago.'); });
        };

        const scriptId = 'paypal-sdk-script';
        const existing = document.getElementById(scriptId);

        if (existing) {
            if ((window as any).paypal) {
                setLoading(false);
                renderButton();
            } else {
                existing.addEventListener('load', () => { if (!cancelled) { setLoading(false); renderButton(); } });
            }
        } else {
            const script = document.createElement('script');
            script.id = scriptId;
            script.src = `https://www.paypal.com/sdk/js?client-id=${PAYPAL_CLIENT_ID}&vault=true&intent=subscription`;
            script.setAttribute('data-sdk-integration-source', 'button-factory');
            script.onload = () => { if (!cancelled) { setLoading(false); renderButton(); } };
            script.onerror = () => { if (!cancelled) { setError('No se pudo cargar PayPal.'); setLoading(false); } };
            document.body.appendChild(script);
        }

        return () => {
            cancelled = true;
            if (containerRef.current) containerRef.current.innerHTML = '';
        };
    }, [plan]);

    return (
        <div className="w-full">
            {loading && (
                <div className="flex items-center justify-center gap-2 py-3 text-slate-400 text-sm">
                    <div className="w-4 h-4 border-2 border-slate-600 border-t-indigo-400 rounded-full animate-spin" />
                    Cargando PayPal...
                </div>
            )}
            {error && (
                <p className="text-brand-danger text-xs text-center py-1">{error}</p>
            )}
            <div ref={containerRef} className={loading ? 'hidden' : ''} />
        </div>
    );
};

export default PayPalButton;
